import { formatCurrency } from '../../shared/utils';

export const TIER_MAP = {
  essential: 'Essential Plan',
  premium: 'Premium Plan',
  'pawsome-employer': 'Pawsome Employer',
  'essential-puppy': 'Puppy Essential',
  'essential-dog': 'Dog Essential',
  'essential-kitten': 'Kitten Essential',
  'essential-cat': 'Cat Essential',
  'premium-puppy': 'Puppy Premium',
  'premium-dog': 'Dog Premium',
  'premium-kitten': 'Kitten Premium',
  'premium-cat': 'Cat Premium',
  'elite-puppy': 'Puppy Elite',
  'elite-dog': 'Dog Elite',
  'elite-kitten': 'Kitten Elite',
  'elite-cat': 'Cat Elite',
};

const SPECIES_MAP = {
  dog: 'Puppy/Dog',
  cat: 'Kitten/Cat',
};

/**
 * Parses wellness program data, transforming it into a array of key and value.
 *
 * @param {Object} data - The input data containing wellness program information e.g. {"puppy": {"basic":{}}}.
 * @returns {Object} - The transformed data with wellness programs in array of key and value.
 */
export function parseWellnessPrograms(data) {
  const { program_options: programOptions, ...rest } = data;

  const transformedObj = {};

  // Check if programOptions exist in the input data
  if (programOptions) {
    for (const key in programOptions) {
      transformedObj[key] = {};

      for (const innerKey in programOptions[key]) {
        transformedObj[key][innerKey] = [];

        for (const prop in programOptions[key][innerKey]) {
          transformedObj[key][innerKey].push({
            key: prop,
            value: programOptions[key][innerKey][prop],
          });
        }
      }
    }
  }

  return { ...rest, program_options: transformedObj };
}

export function setProgramOptionsItems(option, type, wellnessProducts = []) {
  let keys = [];
  switch (type) {
    case 'wellnessCare':
      keys = ['exams'];
      break;
    case 'diseasePrevention':
      keys = ['vaccines', 'heartworm_meds'];
      break;
    case 'diagnostics':
      keys = ['fecal', 'bloodwork'];
      break;
    case 'preventiveProcedures':
      keys = ['spay_neuter_OR_dental'];
      break;
    case 'everydayCare':
      keys = ['grooming'];
      break;
    case 'support':
      keys = ['telehealth'];
      break;
    default:
      break;
  }

  const wellnessProductsWithTelehealth = [
    ...wellnessProducts,
    { entitlement: 'TELEHEALTH', name: 'PawSupport 24/7 Pet Health Helpline' },
  ];

  const items = option
    .filter(obj => keys.includes(obj.key) && obj.value !== '0')
    .map(item => {
      return {
        label: wellnessProductsWithTelehealth.find(
          product => product.entitlement === item.key.toUpperCase()
        )?.name,
        value:
          item.value === 'unlimited' || item.value === '99999'
            ? 'Unlimited'
            : `Up to ${formatCurrency(item.value, {
                hideZero: true,
              })}`,
      };
    });

  return items;
}

export function transformDeclineReasonOptionsResponse(response) {
  const cost_decline_reasons = response
    .filter(reason => reason.category === 'COST')
    .sort((a, b) => a.admin_visual_text.localeCompare(b.admin_visual_text));

  const other_decline_reasons = response
    .filter(reason => reason.category === 'OTHER')
    .sort((a, b) => a.admin_visual_text.localeCompare(b.admin_visual_text));

  cost_decline_reasons.unshift({
    id: 'cost',
    admin_visual_text: 'Cost',
    disabled: true,
  });
  other_decline_reasons.unshift({
    id: 'other',
    admin_visual_text: 'Other',
    disabled: true,
  });
  const result = cost_decline_reasons.concat(other_decline_reasons);
  return result.map(reason => ({
    id: reason.id,
    name: reason.admin_visual_text,
    disabled: reason.disabled || false,
  }));
}

export function getTier(tier, program_id) {
  const wellnessVersion = String(program_id).split('-')[0].replace('saw', '');

  // Ensure tier is a string before calling replaceAll.
  const tierStr = String(tier);
  const tierLabel =
    TIER_MAP[tierStr] ||
    TIER_MAP[tierStr.replace(/\./g, '-')] ||
    tierStr
      .replace(/\./g, ' ')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

  return `${tierLabel} Plan - ${wellnessVersion}`;
}

export function getEmployerTier(tier, program_id) {
  const tierKey = tier.split('.')[0];
  const tierPlan = tier.split('.')[1].split('-')[0];
  const wellnessVersion = program_id.split('-')[0].replace('saw', '');
  const wellnessPartner = program_id.split('-')[2];

  const keyValue = TIER_MAP[tierKey] || '';
  const planValue = TIER_MAP[tierPlan] || '';

  return `${keyValue} ${planValue} - ${wellnessPartner} - ${wellnessVersion}`.trim();
}

export function getSpecies(tier) {
  const speciesKey = tier.split('-')[2] || tier.split('.')[1];
  return SPECIES_MAP[speciesKey] || '';
}
